import { inject, Injectable } from '@angular/core';
import {
  catchError,
  from,
  interval,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { PhotoQueueService } from './photo-queue.service';
import { WorkflowOfflineService } from './workflow-offline.service';
import type {
  WorkflowPendingWorkItem,
  WorkflowPendingWorkSummary,
} from './workflow-pending-work.types';
import { isStalePhotoQueueTask } from './photo-queue-error';

export const EMPTY_WORKFLOW_PENDING_WORK: WorkflowPendingWorkSummary = {
  pending: 0,
  failed: 0,
  unknown: false,
  items: [],
};

const UNKNOWN_WORKFLOW_PENDING_WORK: WorkflowPendingWorkSummary = {
  ...EMPTY_WORKFLOW_PENDING_WORK,
  unknown: true,
};

/**
 * Незавершённая фоновая работа регламентов текущего владельца:
 * офлайн-сабмиты форм, завершения и задачи фото.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowPendingWorkService {
  private readonly workflowOfflineService = inject(WorkflowOfflineService);
  private readonly photoQueueService = inject(PhotoQueueService);

  async countForOwner(): Promise<number> {
    const [queueCount, photoCount] = await Promise.all([
      this.workflowOfflineService.getQueueCount(),
      this.photoQueueService.getPendingCount(),
    ]);

    return queueCount + photoCount;
  }

  /**
   * Неотправленные сабмиты форм и задачи фото заявки (блокируют завершение регламентов).
   * Ошибка чтения очереди пробрасывается: неизвестное состояние не равно «пусто».
   */
  async countForRequest(requestNumber: string): Promise<number> {
    const summary = await this.getSummaryForRequest(requestNumber);

    return summary.items.filter(item => item.kind !== 'completion').length;
  }

  async getSummaryForRequest(
    requestNumber: string,
  ): Promise<WorkflowPendingWorkSummary> {
    const [submissions, completions, photoTasks] = await Promise.all([
      this.workflowOfflineService.getSubmissionQueue(),
      this.workflowOfflineService.getCompletionQueue(),
      this.photoQueueService.getAll(),
    ]);

    const items: WorkflowPendingWorkItem[] = [
      ...submissions
        .filter(task => task.requestNumber === requestNumber)
        .map(task => ({
          kind: 'form' as const,
          status:
            task.status === 'failed'
              ? ('failed' as const)
              : ('pending' as const),
          requestWorkflowId: task.workflowId,
          nodeId: task.nodeId,
          formId: task.formId,
          error: task.lastError?.message,
        })),
      ...photoTasks
        .filter(
          task =>
            task.target.requestNumber === requestNumber &&
            task.state !== 'succeeded' &&
            !isStalePhotoQueueTask(task),
        )
        .map(task => ({
          kind: 'photo' as const,
          status:
            task.state === 'failed'
              ? ('failed' as const)
              : ('pending' as const),
          requestWorkflowId: task.target.workflowId,
          nodeId: task.target.nodeId,
          formId: task.target.formId,
          error: task.lastError?.message,
        })),
      ...completions
        .filter(task => task.requestNumber === requestNumber)
        .map(task => ({
          kind: 'completion' as const,
          status:
            task.status === 'failed'
              ? ('failed' as const)
              : ('pending' as const),
          error: task.lastError?.message,
        })),
    ];

    return {
      pending: items.filter(item => item.status === 'pending').length,
      failed: items.filter(item => item.status === 'failed').length,
      unknown: false,
      items,
    };
  }

  /**
   * Повторить все упавшие задачи заявки: формы, завершение и фото
   */
  async retryFailedForRequest(requestNumber: string): Promise<void> {
    await this.workflowOfflineService.retryFailedForRequest(requestNumber);

    const photoTasks = await this.photoQueueService.getAll();

    for (const task of photoTasks) {
      if (
        task.target.requestNumber === requestNumber &&
        task.state === 'failed'
      ) {
        await this.photoQueueService.retry(task.id);
      }
    }
  }

  /**
   * Сводка по заявке, обновляемая при изменениях очередей (и периодически для фото)
   */
  watchRequest$(requestNumber: string): Observable<WorkflowPendingWorkSummary> {
    return merge(
      this.workflowOfflineService.queueUpdated$,
      this.photoQueueService.events$,
      interval(15_000),
    ).pipe(
      startWith(null),
      switchMap(() =>
        from(this.getSummaryForRequest(requestNumber)).pipe(
          catchError(error => {
            console.error('Failed to read pending workflow work:', error);

            return of(UNKNOWN_WORKFLOW_PENDING_WORK);
          }),
        ),
      ),
    );
  }
}
